import { ContentState } from "draft-js";
import { motion } from "framer-motion";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { CarouselPostInfos } from "@domain/carousel/dtos";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { useOpenGraphData } from "@domain/media/hooks";
import { Media } from "@domain/media/media";
import { isImage, isVideo } from "@domain/media/utils";
import { Post } from "@domain/post/post";
import { scrollToId } from "@utils/dom";
import { useAsync } from "@utils/hooks";
import { SlidingModal } from "@ui/modals/slidingModal";
import { fadeIn } from "@ui/styles/keyframes";
import { gap } from "@ui/styles/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { Carousel } from "@ui/components/carousel/carousel";
import { PrimaryBold } from "@ui/components/containers/primary";
import { DeleteIcon } from "@ui/components/icons";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { stack } from "@ui/components/layout";
import { LoadingBar } from "@ui/components/loadingBar";
import { AssetBrowser } from "@ui/components/media/assetBrowser/assetBrowser";
import { PdfViewer } from "@ui/components/media/pdf/pdfViewer";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { UserView } from "@ui/components/profile/userView";
import { EditPostMedias } from "./editPostMedias";
import { PostOpenGraphView } from "./postOpenGraphView";

interface EditPostModalProps {
	initialPost?: Post;
	initialAssets?: Media[];
	groupId?: string;
	close: () => void;
}
export const EditPostModal: React.FC<EditPostModalProps> = ({
	initialPost,
	initialAssets,
	groupId = initialPost?.group?.id,
	close,
}) => {
	const { postService } = useServices();
	const [focused, setFocused] = useState(false);
	const [scrollToEndActive, setScrollToEndActive] = useState(true);
	const [assets, setAssets] = useState<Media[]>(initialPost?.assets ?? initialAssets ?? []);
	const [postContent, setPostContent] = useState(initialPost?.text ?? ContentState.createFromText(""));
	const currentProfile = useCurrentProfile();
	const organizationAlias = useOrganizationAlias();
	const { data, setData, loading: dataLoading } = useOpenGraphData(postContent, initialPost?.urlPreview);
	const [browsingAssets, setBrowsingAssets] = useState(false);
	const [videoLoadingProgress, setVideoLoadingProgress] = useState<number | null>(null);
	const [carouselDetails, setCarouselDetails] = useState<CarouselPostInfos | undefined>(undefined);
	const { trackingService } = useServices();
	const { run, loading } = useAsync(
		async (carouselDetails: CarouselPostInfos | undefined) => {
			let post: Post;
			if (initialPost) {
				post = await postService.updatePost(initialPost.id, {
					assets,
					text: postContent,
					urlPreview: data ?? undefined,
					groupId,
					carouselDetails,
				});
			} else {
				post = await postService.createPost({
					text: postContent,
					assets,
					urlPreview: data ?? undefined,
					groupId,
					carouselDetails,
				});

				if (carouselDetails?.title) {
					trackingService.track("Carousel - created carousel");
				}

				postService
					.fetchFeed(groupId ?? organizationAlias?.id, organizationAlias ? "organization" : groupId ? "group" : "user")
					.then(() => scrollToId(post.id));
			}
			close();
		},
		[initialPost, assets, postContent, data, organizationAlias?.id]
	);

	const removeAsset = (asset: Media): void => {
		const index = assets.indexOf(asset);
		if (index > -1) {
			assets.splice(index, 1);
		}
		setAssets([...assets]);
		setScrollToEndActive(false);
	};

	return (
		<Container
			sharedTitle
			close={close}
			currentIndex={+browsingAssets}
			title={I18n.t(initialPost ? "post.edit.title" : "post.create.title")}
			elements={[
				<motion.form
					layout
					key="form"
					onSubmit={(e) => {
						e.preventDefault();
						run(carouselDetails);
					}}
				>
					{currentProfile && <BigUserView pictureSize={"M"} user={organizationAlias ?? currentProfile} disabled />}
					<InputWrapper focused={focused}>
						<StyledInput
							autoFocus
							mentionsEnabled
							placeholder={I18n.t("post.input.placeholder")}
							value={postContent}
							onChange={(v) => setPostContent(v)}
							onFocus={() => setFocused(true)}
							onBlur={() => setFocused(false)}
							richStyleEnabled={false}
						/>
						{assets.length === 1 ? (
							assets[0].mimeType === "application/pdf" ? (
								<AssetWrapper>
									<PdfViewer
										emitCarouselInfos={(carouselDetails: CarouselPostInfos | undefined) => {
											setCarouselDetails(carouselDetails);
										}}
										postCreation
										source={assets[0].url}
										pdfHeight={400}
									/>
									<DeleteIcon onClick={() => removeAsset(assets[0])} />
								</AssetWrapper>
							) : assets[0].mimeType.startsWith("video") ? (
								<VideoWrapper>
									<TooshareVideoPlayer
										asset={assets[0]}
										onAssetUpdated={(asset) => setAssets([asset])}
										disablePictureInPicture
									/>
									<DeleteIcon onClick={() => removeAsset(assets[0])} />
								</VideoWrapper>
							) : (
								<PostImage hasFixedHeight>
									<img src={assets[0].url} />
									<DeleteIcon onClick={() => removeAsset(assets[0])} />
								</PostImage>
							)
						) : videoLoadingProgress !== null ? (
							<LoadingBar progress={videoLoadingProgress} />
						) : assets.length > 1 ? (
							<Carousel
								items={assets}
								itemWidth={200}
								itemHeight={200}
								hideArrowNavigation
								isScrollToEndEnabled={scrollToEndActive}
								renderItem={(asset) => (
									<PostImage key={asset.id}>
										<img src={asset.url} />
										<DeleteIcon onClick={() => removeAsset(asset)} />
									</PostImage>
								)}
								loading={dataLoading || loading}
							/>
						) : dataLoading ? (
							<ClipLoader size={24} />
						) : data ? (
							<PostOpenGraphView data={data} clearData={() => setData(null)} />
						) : null}
					</InputWrapper>
					<Bottom>
						<PrimaryBold>{I18n.t("post.edit.medias")}</PrimaryBold>
						<EditPostMedias
							imagesOrAssetManagerDisabled={!!assets.find((a) => !isImage(a))}
							pdfDisabled={assets.length > 0}
							onImagesUploaded={(medias) => {
								setAssets([...assets, ...medias]);
								setScrollToEndActive(true);
							}}
							onPdfOrVideoUploaded={(media) => {
								setAssets([media]);
								setVideoLoadingProgress(null);
							}}
							onAssetBrowserOpened={() => setBrowsingAssets(true)}
							onVideoUploadStart={() => setVideoLoadingProgress(0)}
							onVideoProgress={(progress) => setVideoLoadingProgress(progress)}
							onUploadCancel={() => setVideoLoadingProgress(null)}
						/>
						<PublishButton
							loading={loading}
							type="submit"
							disabled={
								(!postContent.getPlainText().trim() && assets.length < 1) ||
								(!!assets[0] && isVideo(assets[0]) && assets[0].metadata?.status !== "COMPLETED") ||
								(!!assets[0] &&
									assets[0].mimeType === "application/pdf" &&
									!!carouselDetails &&
									(!carouselDetails.title ||
										carouselDetails.title.trim().length === 0 ||
										carouselDetails.title.length > 100 ||
										!carouselDetails.language ||
										!carouselDetails.category))
							}
						>
							{I18n.t("post.input.submit")}
						</PublishButton>
					</Bottom>
				</motion.form>,
				<AssetBrowser
					key="assets"
					multiple
					onAssetsChosen={(a) => {
						setAssets([...assets, ...a]);
						setBrowsingAssets(false);
					}}
					allowedTypes={assets.length > 0 ? ["image/*"] : ["image/*", "application/pdf", "video/*"]}
					onCancel={() => setBrowsingAssets(false)}
					small
				/>,
			]}
		/>
	);
};

const Container = styled(SlidingModal)`
	${gap(20)};
	form {
		${gap(20)};
	}
`;

const InputWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "focused",
})<{ focused: boolean }>`
	display: flex;
	flex-direction: column;
	width: 100%;
	border-radius: 7px;
	transition: border-color 0.2s linear;
	background-color: ${({ theme }) => theme.colors.white};
`;

const StyledInput = styled(RichTextInput)`
	flex: 1;
	margin-bottom: 20px;
	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 10px 0;
		font-size: 0.9375rem;
	}
	.public-DraftEditorPlaceholder-root {
		font-weight: 400;
		opacity: 0.8;
	}
`;

const PostImage = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "hasFixedHeight",
})<{ hasFixedHeight?: boolean }>`
	position: relative;
	${({ hasFixedHeight }) =>
		hasFixedHeight &&
		css`
			width: fit-content;
			margin: auto;
		`};
	img {
		${({ hasFixedHeight }) => (hasFixedHeight ? "max-width: 100%" : "width: 100%")};
		max-height: ${({ hasFixedHeight }) => (hasFixedHeight ? "400px" : "100%")};
		overflow: hidden;
		border-radius: 9px;
	}
	svg {
		cursor: pointer;
		position: absolute;
		right: 0;
		top: 0;
	}
`;

const AssetWrapper = styled.div`
	position: relative;

	> svg {
		cursor: pointer;
		position: absolute;
		right: 0;
		top: 0;
	}
`;

const PublishButton = styled(LoadingButton)`
	animation: ${fadeIn} 0.1s linear;
`;

const BigUserView = styled(UserView)`
	${({ theme }) => theme.textStyles.primary};
`;

const Bottom = styled.div`
	${stack(20)};
`;

const VideoWrapper = styled(AssetWrapper)`
	position: relative;
	.quality-control {
		display: none;
	}
`;
